import { useCallback, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import type { ModuleQueryKeys } from "./types";
import type { ListItem } from "./types";

// ─── Core rename mutation hook ─────────────────────────────────────

interface UseEntityRenameParams {
  readonly rpcMethod: string | undefined;
  readonly entityId: string | undefined;
  readonly queryKeys: ModuleQueryKeys;
  readonly runtime: AppRuntime;
  readonly mapParams?: (id: string, name: string) => Record<string, unknown>;
  /** Called during optimistic update for page 2+ items not in RQ cache. */
  readonly onPatchItem?: (id: string, name: string) => void;
}

interface UseEntityRenameResult {
  /** Rename the default entity (header). Uses the fixed entityId. */
  readonly rename: (name: string) => void;
  /** Rename any entity by explicit ID (list items). @tested-by: tst_fe_scn_list_rename_001 */
  readonly renameEntity: (id: string, name: string) => void;
}

const NOOP: UseEntityRenameResult = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  rename: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  renameEntity: () => {},
};

export function useEntityRename({
  rpcMethod,
  entityId,
  queryKeys,
  runtime,
  mapParams,
  onPatchItem,
}: UseEntityRenameParams): UseEntityRenameResult {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    unknown,
    Error,
    { targetId: string; name: string },
    {
      previousList: { items: ListItem[] } | undefined;
      previousDetails: [readonly unknown[], Record<string, unknown> | undefined][];
      previousName: string | null;
    }
  >({
    mutationFn: ({ targetId, name }) => {
      const params = mapParams
        ? mapParams(targetId, name)
        : { id: targetId, name };
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return runtime.transport.rpc(rpcMethod!, params);
    },
    onMutate: async ({ targetId, name }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.all });
      await queryClient.cancelQueries({ queryKey: queryKeys.detail(targetId) });

      const previousList = queryClient.getQueryData<{ items: ListItem[] }>(
        queryKeys.all,
      );
      const previousDetails = queryClient.getQueriesData<Record<string, unknown>>({
        queryKey: queryKeys.detail(targetId),
      });

      // Remember original name for page 2+ rollback
      const previousName = previousList?.items.find((i) => i.id === targetId)?.name ?? null;

      // Optimistic update on list (page 1 — React Query cache)
      queryClient.setQueriesData<{ items: ListItem[] }>(
        { queryKey: queryKeys.all },
        (old) => {
          if (!old?.items) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.id === targetId ? { ...item, name } : item,
            ),
          };
        },
      );

      // Optimistic update on page 2+ items (local state in useModuleList)
      onPatchItem?.(targetId, name);

      // Optimistic update on detail (patch both `name` and `title` for modules that use either)
      queryClient.setQueriesData<Record<string, unknown>>(
        { queryKey: queryKeys.detail(targetId) },
        (old: Record<string, unknown> | undefined) =>
          old ? { ...old, name, title: name } : old,
      );

      return { previousList, previousDetails, previousName };
    },
    onError: (_err, vars, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(queryKeys.all, context.previousList);
      }
      if (context?.previousDetails) {
        for (const [queryKey, data] of context.previousDetails) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      // Rollback page 2+ items
      if (context?.previousName != null) {
        onPatchItem?.(vars.targetId, context.previousName);
      }
    },
    onSettled: (_data, _err, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.all });
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (vars) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.detail(vars.targetId),
        });
      }
    },
  });

  if (!rpcMethod) {
    return NOOP;
  }

  return {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    rename: (name: string) => {
      if (!entityId) return;
      mutation.mutate({ targetId: entityId, name });
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    renameEntity: (id: string, name: string) => {
      mutation.mutate({ targetId: id, name });
    },
  };
}

// ─── List rename editing state ─────────────────────────────────────

interface UseListRenameStateParams {
  readonly renameEntity: (id: string, name: string) => void;
}

export interface UseListRenameStateResult {
  readonly editingItemId: string | null;
  readonly startRename: (id: string) => void;
  /** Commit rename. No-ops if cancelled, empty, or unchanged. @tested-by: tst_fe_scn_list_rename_001 */
  readonly commitRename: (id: string, originalName: string | null, newName: string) => void;
  /** Cancel rename. Prevents subsequent blur from committing. @tested-by: tst_fe_scn_list_rename_004 */
  readonly cancelRename: () => void;
}

export function useListRenameState({
  renameEntity,
}: UseListRenameStateParams): UseListRenameStateResult {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  const startRename = useCallback((id: string) => {
    cancelledRef.current = false;
    setEditingItemId(id);
  }, []);

  const commitRename = useCallback(
    (id: string, originalName: string | null, newName: string) => {
      if (cancelledRef.current) return;
      cancelledRef.current = true; // block subsequent blur after Enter
      setEditingItemId(null);
      const trimmed = newName.trim();
      if (!trimmed || trimmed === (originalName ?? "")) return;
      renameEntity(id, trimmed);
    },
    [renameEntity],
  );

  const cancelRename = useCallback(() => {
    cancelledRef.current = true;
    setEditingItemId(null);
  }, []);

  return { editingItemId, startRename, commitRename, cancelRename };
}
